import React, { useState, useEffect } from 'react';
import { GamesTab, NewsTab, UsersTab, SettingsTab, DashboardTab, MassEmailTab } from '../components/admin';
import DrinksTab from '../components/admin/DrinksTab';
import './AdminPanel.css';
import { settingsAPI, userAPI, gameAPI, newsAPI } from "../utils/api";
import { toast } from "react-toastify";

const AdminPanel = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [games, setGames] = useState([]);
  const [news, setNews] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [settings, setSettings] = useState({});
  const [selectedGame, setSelectedGame] = useState(null);
  const [showAddGame, setShowAddGame] = useState(false);
  const [showAddNews, setShowAddNews] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadInitialData();
  }, [activeTab]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      
      if (activeTab === 'games') {
        await loadGames();
      } else if (activeTab === 'news') {
        await loadNews();
      } else if (activeTab === 'users') {
        await loadPendingUsers();
      } else if (activeTab === 'settings') {
        await loadSettings();
      }
    } finally {
      setLoading(false);
    }
  };

  const loadGames = async () => {
    try {
      const response = await gameAPI.getAllGames();
      if (response.success) {
        const transformedGames = await Promise.all(
          response.games.map(async (game) => {
            try {
              const gameDetailResponse = await gameAPI.getGame(game.id);
              if (gameDetailResponse.success && gameDetailResponse.game.registrations) {
                const transformedRegistrations = gameDetailResponse.game.registrations.map(reg => ({
                  id: reg.id,
                  userId: reg.user_id,
                  firstName: reg.first_name || '',
                  lastName: reg.last_name || '',
                  name: reg.first_name && reg.last_name ?
                    `${reg.first_name} ${reg.last_name}` : reg.username || 'Unbekannt',
                  email: reg.email || '',
                  ticketType: reg.ticket_type,
                  drink: reg.drink || 'bier',
                  message: reg.message || '',
                  // Champions League Felder
                  address: reg.address || '',
                  idCardNumber: reg.id_card_number || '',
                  dateOfBirth: reg.date_of_birth || '',
                  placeOfBirth: reg.place_of_birth || '',
                  registeredAt: reg.created_at ? reg.created_at.split(' ')[0] : '',
                  status: reg.status || 'pending'
                }));

                return {
                  id: game.id,
                  opponent: game.opponent,
                  date: game.game_date,
                  time: game.game_time,
                  location: game.location,
                  category: game.category,
                  description: game.description || '',
                  maxRegistrations: game.max_registrations || 50,
                  registrations: transformedRegistrations
                };
              }
            } catch (detailError) {
              console.error('Fehler beim Laden der Spieldetails:', detailError);
            }

            return {
              id: game.id,
              opponent: game.opponent,
              date: game.game_date,
              time: game.game_time,
              location: game.location,
              category: game.category,
              description: game.description || '',
              maxRegistrations: game.max_registrations || 50,
              registrations: []
            };
          })
        );
        setGames(transformedGames);
      }
    } catch (error) {
      console.error('Fehler beim Laden der Spiele:', error);
      toast.error('Fehler beim Laden der Spiele');
      setGames([]);
    }
  };

  const loadNews = async () => {
    try {
      const response = await newsAPI.getAllNews();
      if (response.success) {
        setNews(response.news);
      }
    } catch (error) {
      console.error('Fehler beim Laden der News:', error);
      toast.error('Fehler beim Laden der News');
    }
  };

  const loadPendingUsers = async () => {
    try {
      const response = await userAPI.getPendingUsers();
      if (response.success) {
        setPendingUsers(response.users || []);
      } else {
        setPendingUsers([]);
      }
    } catch (error) {
      console.error('Fehler beim Laden der registrierten User:', error);
      toast.error('Fehler beim Laden der User');
      setPendingUsers([]);
    }
  };

  const loadSettings = async () => {
    try {
      const response = await settingsAPI.getSettings();
      if (response.success) {
        setSettings(response.settings);
      }
    } catch (error) {
      console.error('Fehler beim Laden der Einstellungen:', error);
      toast.error('Fehler beim Laden der Einstellungen');
    }
  };

  const renderActiveTab = () => {
    switch (activeTab) {
      case 'dashboard':
        return <DashboardTab />;
      case 'email':
        return <MassEmailTab />;
      case 'games':
        return (
          <GamesTab
            games={games}
            setGames={setGames}
            selectedGame={selectedGame}
            setSelectedGame={setSelectedGame}
            showAddGame={showAddGame}
            setShowAddGame={setShowAddGame}
            loading={loading}
            setLoading={setLoading}
            loadGames={loadGames}
          />
        );
      case 'news':
        return (
          <NewsTab
            news={news}
            setNews={setNews}
            showAddNews={showAddNews}
            setShowAddNews={setShowAddNews}
            loading={loading}
            setLoading={setLoading}
            loadNews={loadNews}
          />
        );
      case 'users':
        return (
          <UsersTab
            pendingUsers={pendingUsers}
            setPendingUsers={setPendingUsers}
            loadPendingUsers={loadPendingUsers}
          />
        );
      case 'settings':
        return (
          <SettingsTab
            settings={settings}
            setSettings={setSettings}
            loadSettings={loadSettings}
          />
        );
      case 'drinks':
        return <DrinksTab />;
      default:
        return null;
    }
  };

  return (
    <div className="container">
      <div className="admin-panel">
        <div className="admin-header">
          <h1>Admin Panel</h1>
        </div>

        <div className="admin-tabs">
          <button
            className={`tab ${activeTab === 'dashboard' ? 'active' : ''}`}
            onClick={() => setActiveTab('dashboard')}
          >
            Übersicht
          </button>
          <button
            className={`tab ${activeTab === 'games' ? 'active' : ''}`}
            onClick={() => setActiveTab('games')}
          >
            Spiele
          </button>
          <button
            className={`tab ${activeTab === 'news' ? 'active' : ''}`}
            onClick={() => setActiveTab('news')}
          >
            News
          </button>
          <button
            className={`tab ${activeTab === 'users' ? 'active' : ''}`}
            onClick={() => setActiveTab('users')}
          >
            Benutzer
          </button>
          <button
            className={`tab ${activeTab === 'drinks' ? 'active' : ''}`}
            onClick={() => setActiveTab('drinks')}
          >
            Getränke
          </button>
          <button
            className={`tab ${activeTab === 'settings' ? 'active' : ''}`}
            onClick={() => setActiveTab('settings')}
          >
            Einstellungen
          </button>
          <button
            className={`tab ${activeTab === 'email' ? 'active' : ''}`}
            onClick={() => setActiveTab('email')}
          >
            E-Mail
          </button>
        </div>

        <div className="admin-content">
          {renderActiveTab()}
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;